import { Link } from "react-router-dom";
import { Home, ArrowRight, Target } from "lucide-react";
import { useSeo } from "../lib/useSeo";

const SITE_URL = "https://shillongmorningteerresulttoday.com";

/**
 * Friendly 404 page — shown by React Router when no route matches.
 * Includes helpful links back to popular Teer pages so users don't leave.
 */
const NotFound = () => {
    useSeo({
        title: "Page Not Found (404) – Shillong Morning Teer Result Today",
        description:
            "The page you are looking for does not exist. Return to the homepage or browse popular Shillong Teer, Juwai Teer, Khanapara Teer and Night Teer result pages.",
        keywords: "404, page not found, Shillong Teer",
        path: "/404",
        image: `${SITE_URL}/og-image.png`,
        imageAlt: "Page not found",
    });

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
            <div className="max-w-2xl w-full text-center" data-testid="not-found-page">
                {/* Big 404 with target icon */}
                <div className="relative inline-flex items-center justify-center mb-6">
                    <Target className="h-24 w-24 md:h-32 md:w-32 text-yellow-400" strokeWidth={1.5} />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="font-num font-black text-3xl md:text-4xl text-blue-950">
                            404
                        </span>
                    </div>
                </div>

                <h1
                    className="font-display font-bold text-3xl md:text-5xl text-blue-950 tracking-tight"
                    data-testid="not-found-h1"
                >
                    Page Not Found
                </h1>
                <p className="mt-3 text-slate-600 text-base md:text-lg">
                    Jo page aap dhoondh rahe ho wo yahan nahi hai. Ho sakta hai link purana ho
                    ya URL mein typo ho.
                </p>

                {/* Primary CTA */}
                <div className="mt-8">
                    <Link
                        to="/"
                        data-testid="nf-back-home"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-700 text-white text-sm font-bold hover:bg-blue-800 transition-colors shadow-md"
                    >
                        <Home className="h-4 w-4" /> Back to Home
                    </Link>
                </div>

                {/* Popular pages */}
                <div className="mt-10 border-t border-slate-200 pt-8 text-left">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold text-center mb-4">
                        Or browse popular Teer pages
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {POPULAR_PAGES.map(({ to, label }) => (
                            <Link
                                key={to}
                                to={to}
                                data-testid={`nf-link-${to}`}
                                className="flex items-center justify-between px-4 py-3 rounded-lg bg-white border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm font-semibold text-blue-900"
                            >
                                <span>{label}</span>
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const POPULAR_PAGES = [
    { to: "/shillong-morning-teer-result-today", label: "Shillong Morning Teer Result Today" },
    { to: "/shillong-teer-result-today", label: "Shillong Teer Result Today" },
    { to: "/khanapara-teer-result-today", label: "Khanapara Teer Result Today" },
    { to: "/juwai-teer-result-today", label: "Juwai Teer Result Today" },
    { to: "/night-teer-result-today", label: "Night Teer Result Today" },
    { to: "/previous-results", label: "All Previous Results" },
];

export default NotFound;
